




import { defineComponent, ref, onMounted, computed } from '@nuxtjs/composition-api';
import { VueInstance } from '@vueuse/core';
import ContentAnnotation from '~/components/partials/Content/ContentAnnotation.vue';
import ContentBodyText from '~/components/partials/Content/ContentBodyText.vue';
import ContentBoxText from '~/components/partials/Content/ContentBoxText.vue';
import ContentContact from '~/components/partials/Content/ContentContact.vue';
import ContentDivider from '~/components/partials/Content/ContentDivider.vue';
import ContentEncloseText from '~/components/partials/Content/ContentEncloseText.vue';
import ContentEncloseTextBlack from '~/components/partials/Content/ContentEncloseTextBlack.vue';
import ContentHeadline from '~/components/partials/Content/ContentHeadline.vue';
import ContentHeadlineWithoutOption from '~/components/partials/Content/ContentHeadlineWithoutOption.vue';
import ContentHistoryText from '~/components/partials/Content/ContentHistoryText.vue';
import ContentHostShops from '~/components/partials/Content/ContentHostShops.vue';
import ContentImage from '~/components/partials/Content/ContentImage.vue';
import ContentImageWithAnnotation from '~/components/partials/Content/ContentImageWithAnnotation.vue';
import ContentMagazineCard from '~/components/partials/Content/ContentMagazineCard.vue';
import ContentPdfButton from '~/components/partials/Content/ContentPdfButton.vue';
import ContentPersonalIntroduction from '~/components/partials/Content/ContentPersonalIntroduction.vue';
import ContentPersonalName from '~/components/partials/Content/ContentPersonalName.vue';
import ContentProductBodyText from '~/components/partials/Content/ContentProductBodyText.vue';
import ContentProductHeadline from '~/components/partials/Content/ContentProductHeadline.vue';
import ContentProductImage from '~/components/partials/Content/ContentProductImage.vue';
import ContentProductIntroductions from '~/components/partials/Content/ContentProductIntroductions.vue';
import ContentRelatedEvents from '~/components/partials/Content/ContentRelatedEvents.vue';
import ContentRelatedItems from '~/components/partials/Content/ContentRelatedItems.vue';
import ContentRelatedShops from '~/components/partials/Content/ContentRelatedShops.vue';
import ContentSeparation from '~/components/partials/Content/ContentSeparation.vue';
import ContentSingleButton from '~/components/partials/Content/ContentSingleButton.vue';
import ContentSingleButtonBlack from '~/components/partials/Content/ContentSingleButtonBlack.vue';
import ContentStaffDetailCard from '~/components/partials/Content/ContentStaffDetailCard.vue';
import ContentStaffReview from '~/components/partials/Content/ContentStaffReview.vue';
import ContentTableOfContents from '~/components/partials/Content/ContentTableOfContents.vue';
import ContentTalkText from '~/components/partials/Content/ContentTalkText.vue';
import ContentTwoColumnButton from '~/components/partials/Content/ContentTwoColumnButton.vue';
import ContentTwoColumnButtonBlack from '~/components/partials/Content/ContentTwoColumnButtonBlack.vue';
import ContentWineIntroCard from '~/components/partials/Content/ContentWineIntroCard.vue';
import ContentWineIntroductions from '~/components/partials/Content/ContentWineIntroductions.vue';
import ContentWraparoundImageText from '~/components/partials/Content/ContentWraparoundImageText.vue';
import ContentYoutube from '~/components/partials/Content/ContentYoutube.vue';
import { isInsidePeriod } from '~/utils';

const components = {
  ContentAnnotation,
  ContentBodyText,
  ContentBoxText,
  ContentContact,
  ContentDivider,
  ContentEncloseText,
  ContentEncloseTextBlack,
  ContentHeadline,
  ContentHeadlineWithoutOption,
  ContentHistoryText,
  ContentImage,
  ContentImageWithAnnotation,
  ContentMagazineCard,
  ContentPersonalIntroduction,
  ContentPdfButton,
  ContentPersonalName,
  ContentProductHeadline,
  ContentProductBodyText,
  ContentProductImage,
  ContentProductIntroductions,
  ContentRelatedEvents,
  ContentRelatedItems,
  ContentRelatedShops,
  ContentSeparation,
  ContentTableOfContents,
  ContentTalkText,
  ContentWineIntroductions,
  ContentSingleButton,
  ContentTwoColumnButton,
  ContentSingleButtonBlack,
  ContentTwoColumnButtonBlack,
  ContentWraparoundImageText,
  ContentYoutube,
  ContentHostShops,
  ContentStaffReview,
  ContentStaffDetailCard,
  ContentWineIntroCard,
};

// dynamic parts
// keyはレスポンスのデータと紐付き、valueはコンポーネント名と紐づく
const map: Record<string, keyof typeof components> = {
  annotation: 'ContentAnnotation',
  body_text: 'ContentBodyText',
  box_text: 'ContentBoxText',
  contact: 'ContentContact',
  divider: 'ContentDivider',
  enclose_text: 'ContentEncloseText',
  enclose_text_black: 'ContentEncloseTextBlack',
  headline: 'ContentHeadline',
  headline_without_option: 'ContentHeadlineWithoutOption',
  history_text: 'ContentHistoryText',
  image: 'ContentImage',
  image_with_annotation: 'ContentImageWithAnnotation',
  magazine_card: 'ContentMagazineCard',
  personal_introduction: 'ContentPersonalIntroduction',
  pdf_button: 'ContentPdfButton',
  personal_name: 'ContentPersonalName',
  product_body_text: 'ContentProductBodyText',
  product_headline: 'ContentProductHeadline',
  product_image: 'ContentProductImage',
  product_introduction: 'ContentProductIntroductions',
  events: 'ContentRelatedEvents',
  related_products: 'ContentRelatedItems',
  related_shops: 'ContentRelatedShops',
  separation: 'ContentSeparation',
  table_of_contents: 'ContentTableOfContents',
  talk_text: 'ContentTalkText',
  wine_introduction: 'ContentWineIntroductions',
  button: 'ContentSingleButton',
  two_colum_button: 'ContentTwoColumnButton',
  button_black: 'ContentSingleButtonBlack',
  two_colum_button_black: 'ContentTwoColumnButtonBlack',
  wraparound_image_text: 'ContentWraparoundImageText',
  youtube: 'ContentYoutube',
  host_shops: 'ContentHostShops',
  staff_id: 'ContentStaffDetailCard',
  staff_review_id: 'ContentStaffReview',
  wine_introduction_card: 'ContentWineIntroCard',
};

export default defineComponent({
  components,
  props: {
    name: {
      type: String,
      required: true,
    },
    items: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      default: undefined,
    },
  },
  setup(props) {
    const dynamicParts = ref<VueInstance>();

    // 表示するか否か
    const isDisplayed = computed((): boolean => {
      // 表示制御設定がなければそのまま表示
      if (!props.items.display_management) {
        return true;
      }
      // 表示フラグがfalseの場合は非表示
      if (!props.items.display_management.is_display) {
        return false;
        // 表示フラグがtrue かつ 表示期間が両方nullの場合は表示
      }

      // 記事に設定されている期限で公開中かどうかを判定
      return isInsidePeriod(props.items.display_management.publish_from, props.items.display_management.publish_to);
    });

    const switchComponent = (type: string) => {
      return map[type];
    };

    onMounted(() => {
      if (!dynamicParts.value || ![map.body_text, map.product_body_text].includes(switchComponent(props.name))) {
        return;
      }

      const dataSrcDOMs = dynamicParts.value.$el.querySelectorAll('[data-src]');
      dataSrcDOMs.forEach((dom) => {
        const dataSrc = dom.getAttribute('data-src');
        if (dataSrc) {
          dom.setAttribute('src', dataSrc);
        }
      });
    });

    return {
      dynamicParts,
      switchComponent,
      props,
      isDisplayed,
    };
  },
});
